import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import cn from 'classnames';
import { Icon, Spinner } from 'components';
import { Modal } from 'components/Modal/Modal.component';
import { Checkbox } from 'components/form';
import { Avatar } from 'components/Avatar/Avatar.component';
import { DropdownV3 } from 'components/ComponentV2/DropdownV3/DropdownV3.component';
import { ButtonV3 } from 'components/ComponentV2';
import { CommunityTypes, CommunityAttributes } from 'lib/models/community';
import { useCommunityInfo } from 'lib/contexts/community-context';
import { ScreeningHeader } from './ScreeningHeader/ScreeningHeader.component';
import { CommunityApplication } from './CommunityApplication';

import styles from './CommunityScreeningModal.module.scss';

interface CommunityScreeningModalProps {
  isOpen: boolean;
  selectedApplicationId?: string;
  selectedCommunityStatus: string;
  viewDashboard: () => void;
  onInviteAttendee: (attendee: {
    first_name: string;
    last_name: string;
    email: string;
  }) => void;
  inviteAttendees: () => Promise<void>;
  inviteProfileViewers: () => Promise<void>;
  inviteEventViewers: () => Promise<void>;
  onSendMessage: (message: string, candidate_ids: string[]) => Promise<void>;
  subscribers_require_approval?: boolean;
  subscription_requests_count?: number;
}

export const CommunityScreeningModal: React.FC<CommunityScreeningModalProps> = (
  props: CommunityScreeningModalProps
) => {
  const {
    isOpen,
    viewDashboard,
    selectedApplicationId,
    selectedCommunityStatus,
    onInviteAttendee,
    inviteAttendees,
    inviteProfileViewers,
    inviteEventViewers,
    onSendMessage,
    subscribers_require_approval,
    subscription_requests_count,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [selectedCommunityOption, setSelectedCommunityOption] =
    useState<string>('');
  const [selectedApplicant, setSelectedApplicant] =
    useState<CommunityAttributes>();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [selectionList, setSelectionList] = useState<{
    [key: string]: CommunityAttributes;
  }>({});
  const [bannerText, setBannerText] = useState('');

  const {
    applications,
    size,
    setSize,
    mutate,
    hasMoreRecords,
    isLoading,
    isLoadingMore,
    isValidating,
    selectedCommunityType,
    onChangeCommunityType,
  } = useCommunityInfo();

  const getBannerText = (type: string) => {
    switch (type) {
      case CommunityTypes.ATTENDEES:
        return 'These are attendees who haven’t joined your community yet.';
      case CommunityTypes.PROFILE_VIEWS:
        return 'These are the unique profile viewers who haven’t joined your community yet.';
      case CommunityTypes.EVENT_VIEWS:
        return 'These are the unique event viewers who haven’t joined your community yet.';
      default:
        return '';
    }
  };
  useEffect(() => {
    const text = getBannerText(selectedCommunityStatus);
    setBannerText(text);

    if (
      selectedCommunityStatus === CommunityTypes.UNSUBSCRIBED ||
      selectedCommunityStatus === CommunityTypes.INVITED
    ) {
      setSelectedCommunityOption(CommunityTypes.SUBSCRIBED);
      onChangeCommunityType(CommunityTypes.SUBSCRIBED);
    } else {
      setSelectedCommunityOption(selectedCommunityStatus);
      onChangeCommunityType(selectedCommunityStatus);
    }
  }, []);

  useEffect(() => {
    return () => {
      setSelectedApplicant(undefined);
    };
  }, []);

  useEffect(() => {
    if (!isLoading && applications && applications.length) {
      setSelectedApplicant(applications[0]);
      setCurrentIndex(0);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isValidating) {
      if (applications.length) {
        viewNextApplication();
      }
    }
  }, [isValidating]);

  useEffect(() => {
    const selected = applications?.find(
      (application: CommunityAttributes) =>
        application.id == selectedApplicationId
    );
    if (applications?.length && selectedApplicationId && selected) {
      const selectedIndex = applications?.findIndex(
        (data: CommunityAttributes) => data.id == selectedApplicationId
      );
      setSelectedApplicant(selected || {});
      setCurrentIndex(selectedIndex || 0);
    }
  }, [selectedApplicationId, applications]);

  const fetchNextData = () => {
    if (hasMoreRecords && !isLoading && !isLoadingMore) {
      setSize(size + 1);
    }
  };

  const handleScroll = () => {
    const container = containerRef.current;
    const loadMoreElement = loadMoreRef.current;

    if (!container || !loadMoreElement) return;

    const containerBottom = container.getBoundingClientRect().bottom;
    const loadMoreTop = loadMoreElement.getBoundingClientRect().top;

    if (loadMoreTop <= containerBottom) {
      fetchNextData();
    }
  };

  const viewNextApplication = () => {
    const selected = applications?.find(
      (application: CommunityAttributes) =>
        application.id == selectedApplicationId
    );
    if (selected) {
      return;
    }
    if (applications[currentIndex]) {
      onSelectApplicant(applications[currentIndex])();
    } else {
      if (applications[currentIndex + 1]) {
        goToNextApplication();
      } else if (applications[currentIndex - 1]) {
        goToPreApplication();
      }
    }
  };

  const goToPreApplication = () => {
    if (currentIndex !== 0) {
      onSelectApplicant(applications[currentIndex - 1])();
    }
  };

  const goToNextApplication = () => {
    if (currentIndex !== applications.length - 1) {
      onSelectApplicant(applications[currentIndex + 1])();
    }
  };

  const onSelectApplicant = (applicant: CommunityAttributes) => () => {
    const index = applications.findIndex(
      (application: CommunityAttributes) => application.id === applicant.id
    );
    setCurrentIndex(index);
    setSelectedApplicant(applicant);
  };

  const toggleSelection =
    (applicant: CommunityAttributes) => (event: React.ChangeEvent) => {
      event.stopPropagation();
      const tempList = JSON.parse(JSON.stringify(selectionList));
      if (tempList[applicant.id]) {
        delete tempList[applicant.id];
      } else {
        tempList[applicant.id] = applicant;
      }
      setSelectionList(tempList);
    };

  const selectAll = () => {
    if (Object.keys(selectionList).length === applications.length) {
      setSelectionList({});
    } else {
      const tempList: { [key: string]: CommunityAttributes } = {};
      applications.forEach(
        (application: CommunityAttributes) =>
          (tempList[application.id] = application)
      );
      setSelectionList(tempList);
    }
  };

  const changeApplicationSelection = (e: React.KeyboardEvent) => {
    const key = e.key;
    if (key === 'ArrowLeft') {
      goToPreApplication();
    } else if (key === 'ArrowRight') {
      goToNextApplication();
    }
  };

  const onScroll = (event: React.UIEvent<HTMLElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > 100);
  };

  const communityOptions = [
    ...(subscribers_require_approval && subscription_requests_count
      ? [
          {
            id: '5',
            label: 'Membership requests',
            value: CommunityTypes.SUBSCRIPTION_REQUESTS,
          },
        ]
      : []),
    {
      id: '1',
      label: 'Members',
      value: CommunityTypes.SUBSCRIBED,
    },
    {
      id: '2',
      label: 'Attendees',
      value: CommunityTypes.ATTENDEES,
    },
    {
      id: '3',
      label: 'Event viewers',
      value: CommunityTypes.EVENT_VIEWS,
    },
    {
      id: '4',
      label: 'Profile viewers',
      value: CommunityTypes.PROFILE_VIEWS,
    },
  ];

  const getNameFromEmail = (email: string) => {
    return email && email.split('@')[0];
  };

  const focusOnEle = () => {
    const ele = document.getElementById('candidate-screening-mode');
    if (ele) {
      ele.scrollIntoView();
    }
  };

  const renderApplications = useMemo(() => {
    return applications.map((application: CommunityAttributes) => {
      const { id, email, first_name, name, avatar_url } = application;

      const isSelectedApplicant = application.id === selectedApplicant?.id;

      const children = (
        <div
          key={id}
          className={cn(styles.candidateCard, {
            [styles.selected]: isSelectedApplicant,
          })}
          onClick={() => {
            onSelectApplicant(application)();
            focusOnEle();
          }}
        >
          <Avatar
            className={styles.avatar}
            name={name || first_name || getNameFromEmail(email)}
            size="medium"
            icon={avatar_url}
          />
          <div className={styles.infoSection}>
            <div className={styles.info}>
              <div className={styles.title}>
                <p className={styles.name}>
                  {name || first_name || getNameFromEmail(email)}
                </p>
              </div>
              <p className={styles.desc}>{email}</p>
            </div>
          </div>
          <div className={styles.selectionContent}>
            <Checkbox
              className={cn(styles.selectionBox, {
                [styles.showSelectionBox]: !!selectionList[id],
              })}
              checked={!!selectionList[id]}
              onChange={toggleSelection(application)}
            />
          </div>
        </div>
      );

      return children;
    });
  }, [applications, selectedApplicant, selectionList]);

  const getSelectedLabel = useCallback(() => {
    const selectedOption = communityOptions.find(
      (item) => item.value === selectedCommunityType
    );
    return selectedOption?.label || '';
  }, [selectedCommunityOption, communityOptions]);

  const handleDropdownChange = useCallback((value) => {
    setSelectedCommunityOption(value);
    const text = getBannerText(value);
    setBannerText(text);

    setSelectionList({});
    setCurrentIndex(0);
    onChangeCommunityType(value);
  }, []);

  const dropdown = useMemo(() => {
    return (
      <DropdownV3
        value={selectedCommunityOption}
        options={communityOptions}
        onChange={handleDropdownChange}
        label={getSelectedLabel()}
        selectedValueLabel={getSelectedLabel()}
        isSearchable={false}
        isValid={true}
        className={styles.actionMenu}
        triggerButtonClassName={styles.actionMenuButton}
        isDefaultTriggerClass={false}
      />
    );
  }, [
    selectedCommunityOption,
    communityOptions,
    handleDropdownChange,
    getSelectedLabel,
    selectedCommunityStatus,
  ]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={viewDashboard}
        isFullScreen
        contentClassName={styles.modalMain}
      >
        {applications && (
          <div
            className={styles.CandidateScreening}
            tabIndex={0}
            onKeyDown={changeApplicationSelection}
            id="candidate-screening-mode"
          >
            <ScreeningHeader
              applications={applications}
              selectionList={selectionList}
              applicantDetails={selectedApplicant}
              goBack={viewDashboard}
              disabledLeftArrow={currentIndex === 0}
              disabledRightArrow={currentIndex === applications.length - 1}
              goToPre={goToPreApplication}
              goToNext={goToNextApplication}
              isScrolled={isScrolled}
              selectionCount={Object.keys(selectionList).length}
              isEmpty={applications.length === 0}
              selectedCommunityOption={selectedCommunityOption}
              onInviteAttendee={onInviteAttendee}
              inviteAttendees={inviteAttendees}
              inviteProfileViewers={inviteProfileViewers}
              inviteEventViewers={inviteEventViewers}
              onSendMessage={onSendMessage}
              bannerText={bannerText}
            />
            <div>
              {isLoading && <Spinner wrapper />}
              {applications.length > 0 && (
                <div
                  className={cn(styles.CandidateScreeningData)}
                  onScroll={onScroll}
                >
                  <div className={cn(styles.candidatesList)}>
                    <div className={styles.header}>
                      <div>
                        <Icon
                          iconName={'icon_users'}
                          size={'mediumplus'}
                          className={styles.headerButton}
                        />
                        {dropdown}
                      </div>
                      <div>
                        <Checkbox
                          hideLabel
                          checked={
                            Object.keys(selectionList).length ===
                            applications.length
                          }
                          onChange={selectAll}
                          className={styles.selectAll}
                        />
                      </div>
                    </div>
                    <div
                      className={styles.applications}
                      onScroll={handleScroll}
                      ref={containerRef}
                    >
                      {renderApplications}
                      <div
                        className={styles.infiteLoader}
                        ref={loadMoreRef}
                      ></div>
                    </div>
                  </div>
                  {selectedApplicant && (
                    <div style={{ width: '68%', marginLeft: '32%' }}>
                      <CommunityApplication
                        applicantDetails={selectedApplicant}
                        status={selectedCommunityOption}
                        refetch={mutate}
                      />
                    </div>
                  )}
                </div>
              )}

              {!applications.length && !isValidating && !isLoading && (
                <div className={cn(styles.CandidateScreeningEmptyData)}>
                  <div className={styles.content}>
                    <div className={styles.action}>
                      <ButtonV3
                        color="secondary"
                        onClick={() => viewDashboard()}
                      >
                        Go to {`community`}
                      </ButtonV3>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CommunityScreeningModal;
